<template>
  <v-tooltip
    location="top"
    :disabled="!refreshDisabled"
    :text="tooltipText"
  >
    <template #activator="{ props }">
      <div v-bind="props">
        <ButtonDropdownOption
          title="Refresh Markers"
          :disabled="refreshDisabled"
          @handle-click="showDialog = true"
        />
      </div>
    </template>
  </v-tooltip>
  <RefreshFeaturesDialog
    :show-dialog="showDialog"
    :loading-refresh="loadingRefresh"
    @close="showDialog = false"
    @refresh-markers="refreshMarkers"
  />
</template>

<script lang="ts">
import ButtonDropdownOption from '@/components/Reusable/Buttons/ButtonDropdownOption.vue';
import RefreshFeaturesDialog from './RefreshFeaturesDialog.vue';

import {SharedMap} from '@/lib/classes/SharedMap';
import {refreshMarkers} from '@/services/RefreshService/RefreshFeatures';
import {RefreshFieldSettings} from '@/services/RefreshService/types';
import {wrapError} from '@northpoint-development/labs-common-core';
import {mapMutations, mapState} from 'vuex';

export default {
  name: 'RefreshFeatures',
  components: {
    ButtonDropdownOption,
    RefreshFeaturesDialog,
  },
  data() {
    return {
      showDialog: false,
      tooltipText: 'No markers with Beyond Maps IDs to refresh',
      loadingRefresh: false,
    };
  },
  computed: {
    ...mapState({sharedMap: (state: any) => state.sharedMap as SharedMap}),
    refreshDisabled() {
      if (this.sharedMap.features.size === 0) return true;
      const placeMarkerFeatures = Array.from(this.sharedMap.features.values())
        .filter((feature) => feature.featureType === 'placeMarker' && feature.originalFeatureMetadata);
      return placeMarkerFeatures.length === 0;
    },
  },
  methods: {
    ...mapMutations(['setSnack']),
    async refreshMarkers(refreshSettings: RefreshFieldSettings) {
      this.loadingRefresh = true;
      let refreshResponse;
      try {
        refreshResponse = await refreshMarkers(this.sharedMap, refreshSettings);
      } catch (error) {
        console.error(wrapError(error, 'Error refreshing markers'));
        this.setSnack({
          message: 'An error occurred while refreshing markers.',
          duration: 5000,
          color: 'error',
        });
        return;
      } finally {
        this.loadingRefresh = false;
        this.showDialog = false;
      }
      let snack;
      if (!refreshResponse?.summary?.errors || refreshResponse?.summary?.errors === 0) {
        snack = {
          message: `Successfully updated placemarkers`,
          duration: 5000,
          color: 'success',
        };
      } else {
        const failedMarkerNames = refreshResponse.results
          .filter((result) => result.status === 'error')
          .map((result) => this.sharedMap.features.get(result.smapFeatureId))
          .filter((feature) => Boolean(feature))
          .map((feature) => feature!.name);
        const failedNamesString = failedMarkerNames.length > 3
          ? `${failedMarkerNames.slice(0, 3).join(', ')} and ${failedMarkerNames.length - 3} more`
          : failedMarkerNames.join(', ');

        snack = {
          message: `Some markers failed to update: ${failedNamesString}`,
          duration: 10000,
          color: 'error',
        };
      }
      this.setSnack(snack);
    },
  },
};
</script>
